import { initBrowserAnalytics } from "@graftus/events/client";

/**
 * Reads Vite public env and initializes the shared OpenPanel web client.
 * Call once from `main.tsx` before the React tree mounts.
 *
 * Env (graftus-webapp-2):
 * - `VITE_OPENPANEL_CLIENT_ID` — app id from OpenPanel (required to send events)
 * - `VITE_OPENPANEL_API_URL` — ingestion API base URL. Self-hosted: must include `/api`, e.g.
 *   `https://your-domain.com/api` (not the dashboard root). Cloud: omit or use default.
 * - `VITE_OPENPANEL_DISABLED` — set `"true"` to turn tracking off
 * - `VITE_OPENPANEL_SEND_IN_DEV` — when `import.meta.env.DEV` is true (local `pnpm dev`, or any build using a mode
 *   where Vite sets DEV): set `"true"` to send real events and enable auto screen / link tracking. Staging should use
 *   a non-development mode (e.g. `vite build --mode staging`) so `DEV` is false and the real client runs.
 * - `VITE_OPENPANEL_DEBUG` — set `"true"` to log OpenPanel SDK traffic to the console
 *
 * Server-side (other packages / Functions): use `@graftus/events/server` with
 * `OPENPANEL_CLIENT_ID`, `OPENPANEL_CLIENT_SECRET`, optional `OPENPANEL_API_URL`,
 * and `OPENPANEL_FORCE_DEV=1` to send outside production.
 */
export function initOpenPanelAnalytics(): void {
  const clientId = import.meta.env.VITE_OPENPANEL_CLIENT_ID ?? "";
  const apiUrl = import.meta.env.VITE_OPENPANEL_API_URL;
  const explicitlyDisabled =
    import.meta.env.VITE_OPENPANEL_DISABLED === "true";
  const sendInDev = import.meta.env.VITE_OPENPANEL_SEND_IN_DEV === "true";
  const debug = import.meta.env.VITE_OPENPANEL_DEBUG === "true";
  const dryRun = import.meta.env.DEV && !sendInDev;
  const enableAutoScreenAndLinks = !import.meta.env.DEV || sendInDev;

  if (import.meta.env.DEV && apiUrl) {
    const isCloudDefault = apiUrl.includes("api.openpanel.dev");
    const endsWithApiPath =
      /\/api\/?$/.test(apiUrl.replace(/\/+$/, "")) || isCloudDefault;
    if (!endsWithApiPath) {
      console.warn(
        "[OpenPanel] VITE_OPENPANEL_API_URL should usually end with `/api` for self-hosted OpenPanel (SDK posts to {apiUrl}/track). " +
          "Example: https://analytics.example.com/api",
      );
    }
  }

  initBrowserAnalytics({
    clientId,
    apiUrl: apiUrl || undefined,
    disabled: explicitlyDisabled || !clientId,
    dryRun,
    trackScreenViews: enableAutoScreenAndLinks,
    trackOutgoingLinks: enableAutoScreenAndLinks,
    trackAttributes: true,
    debug,
    globalProperties: {
      environment: import.meta.env.MODE,
    },
  });
}
